import React, {useState} from 'react';
import cx from 'classnames';

import {
    useFocusFromOpened,
    useModal,
    useModalWithoutPreventScroll,
} from '@pexip/components';

import {isMobileDevice} from '../..';
import {useShouldPanelBeModal} from '../../hooks/useShouldPanelBeModal';

import styles from './SidePanelWrapper.module.scss';

export const SidePanelWrapper: React.FC<
    React.PropsWithChildren<React.ComponentProps<'div'>> & {
        isShown: boolean;
        animateIn?: boolean;
        animateOut?: boolean;
        testId?: string;
        allowScroll?: boolean;
        isNetworkAlertDisplayed?: boolean;
        className?: string;
    }
> = ({
    children,
    className,
    isShown,
    animateIn = false,
    animateOut = false,
    allowScroll = false,
    isNetworkAlertDisplayed = false,
    testId,
    ...props
}) => {
    const [containerElement, setContainerElement] =
        useState<HTMLDivElement | null>(null);
    useFocusFromOpened(containerElement, isShown);

    const shouldEnableModal = useShouldPanelBeModal(isShown);
    const {ref: scrollLockModalRef} = useModal(
        !allowScroll && shouldEnableModal,
    );
    const {ref: modalRef} = useModalWithoutPreventScroll(
        allowScroll && shouldEnableModal,
    );
    const isMobile = isMobileDevice();

    return (
        <div ref={allowScroll ? modalRef : scrollLockModalRef}>
            <div
                data-testid={testId}
                ref={setContainerElement}
                // Focus lands here when the panel opens, so it needs a name to announce.
                role={props['aria-label'] ? 'region' : undefined}
                className={cx(
                    styles.panel,
                    {
                        [styles.show]: isShown,
                        [styles.animateIn]: animateIn,
                        [styles.animateOut]: animateOut,
                        [styles.isMobile]: isMobile,
                        [styles.networkAlert]: isNetworkAlertDisplayed,
                    },
                    className,
                )}
                {...props}
            >
                {children}
            </div>
        </div>
    );
};

export type SidePanelWrapperProps = React.ComponentProps<
    typeof SidePanelWrapper
>;
